import { useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import * as Library from "../../../utils/Library";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import KpiMainWindowHeader from "./metricsComponents/KpiMainWindowHeader";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import HeadlineKPIs from "./metricsComponents/HeadlineKPIs";
import MainWindowHeader, {
    OrganizeBtnMenu,
    CustomSearch
} from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import { useSearchParams } from "react-router-dom";
import GTINsKPIs from "./metricsComponents/GTINsKPIs";
import LayoutContext from "../../../LayoutContext";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.product-kpis-page {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .last-update {
        color: var(--text-icons-gray-100, #d1d1d1);
        text-align: right;

        font-size: 12px;
        font-weight: 400;
        line-height: normal;
        text-transform: none;
    }

    .BO-card-component {
        margin-bottom: 0px !important;
    }
`;

// #endregion

export default function ProductMetricsPage() {
    document.title = `${APP_TITLE} - Product KPIs`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setLayoutType } = useContext(LayoutContext);

    // eslint-disable-next-line no-unused-vars
    const [searchParams] = useSearchParams();
    const urlParamsProductID = searchParams.get("productID");
    const urlParamsProductName = searchParams.get("productName");
    const urlParamsGtinID = searchParams.get("gtinID");
    const navigate = useNavigate();

    //const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const gtinKpisRef = useRef(null);
    const headlineKPIsRef = useRef(null);
    const kpiMainWindowHeaderRef = useRef(null);

    const [kpiData, setKpiData] = useState(null);
    const [kpiEnabled, setKpiEnabled] = useState(false);
    const updateDebounceRef = useRef(null);
    const skipNextFilteredStoresUpdateRef = useRef(false);
    const [currentProduct, setCurrentProduct] = useState(null);

    const [isLoadingRequest, setIsLoadingRequest] = useState(false);
    const [lastUpdatedDateTime, setLastUpdatedDateTime] = useState("--");
    const [dataAsOfDateTime, setDataAsOfDateTime] = useState("--");

    //------- HEADERS -----
    const [allStoresList, setAllStoresList] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    //------- GTIN INFO -----
    const [currentGTIN, setCurrentGTIN] = useState("All");
    const [datePeriod, setDatePeriod] = useState({
        isMaxDate: true,
        fromDate: "", //Format is "YYYY-MM-DD", example: "2025-12-01"
        toDate: "" //Format is "YYYY-MM-DD", example: "2025-12-31"
    });
    const [customSearchValue, setCustomSearchValue] = useState("");

    const endpointRequestParams = {
        productID: urlParamsProductID || undefined,
        gtinID: urlParamsGtinID || undefined,

        filterStoreIDs: filteredStores.map(store => store.value),

        isMaxDate: datePeriod.isMaxDate,
        fromDate: !datePeriod.isMaxDate ? datePeriod.fromDate : undefined,
        toDate: !datePeriod.isMaxDate ? datePeriod.toDate : undefined
    };

    //Remove all undefined values from endpointRequestParams
    Object.keys(endpointRequestParams).forEach(key => {
        if (endpointRequestParams[key] === undefined) delete endpointRequestParams[key];
    });
    // #endregion

    // #region METHODS
    /* const onItemClick = id => {
        gtinKpisRef.current?.onExportClick(id);
    }; */

    const getKPIsRequest = async () => {
        if (!endpointRequestParams.productID && !endpointRequestParams.gtinID) {
            setKpiData(null);
            setKpiEnabled(false);
            return;
        }

        //console.log("getKPIsRequest called", endpointRequestParams);

        const res = await Library.makeApiRequest({
            endpoint: "Stock/getKPIsData",
            data: endpointRequestParams,
            setLoader: setIsLoadingRequest
        });

        if (res?.data?.status !== 200) {
            setKpiData(null);
            setKpiEnabled(false);
            return;
        }

        const data = res?.data?.data;
        setKpiData(data);
        setKpiEnabled(true);
        setLayoutType("List");

        setLastUpdatedDateTime(formatDefaultDateTime(data?.productData?.lastUpdateDate, "--"));
        setDataAsOfDateTime(formatDefaultDateTime(data?.productData?.dateAsOf, "--"));

        const productID = data?.productData?.productID;
        const productName = data?.productData?.productName;
        setCurrentProduct({ productID, productName });

        //const labelBrandID = data?.productData?.labelBrandID;
        //if (!allStoresList?.length) filterConnectedStores(allConnectedStoresList, labelBrandID);
    };

    const handleUpdate = (immediate = false) => {
        if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current);

        if (immediate) {
            getKPIsRequest();
            return;
        }

        updateDebounceRef.current = setTimeout(() => {
            updateDebounceRef.current = null;
            getKPIsRequest();
        }, 50);
    };

    const onStoresChange = (selections, dropdownDataWithSelections) => {
        const isSame = JSON.stringify(selections) === JSON.stringify(filteredStores);
        if (isSame) return;

        setFilteredStores(selections);
        setAllStoresList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        if (data?.stores?.length) {
            const stores = data?.stores?.map(item => {
                const itemLocked = false; //!item?.ffcID;
                //const stockDateAsOf = item?.stockDateAsOf;
                const stockDateAsOfMessage = item?.stockDateAsOfMessage;
                const lastUpdateDate = item?.lastUpdateDate;

                /* let description = "";
                let descriptionColor = "";
                if (!item?.ffcID) {
                    description = "Not affiliated";
                    descriptionColor = "var(--text-icons-gray-300)";
                } else if (stockDateAsOfMessage === "OK") {
                    description = formatDefaultDateTime(lastUpdateDate, "--");
                    descriptionColor = "var(--text-icons-green-500)";
                } else if (stockDateAsOfMessage === "Late") {
                    description = formatDefaultDateTime(lastUpdateDate, "--");
                    descriptionColor = "var(--text-icons-orange-500)";
                } else if (stockDateAsOfMessage === "Not updating") {
                    description = "❌ Not updating";
                    descriptionColor = "var(--text-icons-red-500)";
                } else if (stockDateAsOfMessage === "no data yet") {
                    description = "No data yet";
                    descriptionColor = "var(--text-icons-orange-500)";
                } else {
                    description = "--";
                    descriptionColor = "var(--text-icons-gray-100)";
                } */

                return {
                    name: item?.name,
                    value: item?._id,
                    /*description: description,
                    descriptionColor: descriptionColor,*/
                    locked: itemLocked,
                    selected: itemLocked ? false : true,
                    retailBrandID: item?.retailBrandID
                };
            });

            setAllStoresList(stores);
            setFilteredStores(stores?.filter(item => item?.selected));
        }
        setRequestsEnabled(true);
    };

    const onGtinClicked = gtin => {
        setCurrentGTIN(gtin);
    };

    const resetPage = () => {
        navigate("/products/metrics");

        setKpiData(null);
        setKpiEnabled(false);
        setCurrentProduct(null);
        setIsLoadingRequest(false);
        setLastUpdatedDateTime("--");
        setDataAsOfDateTime("--");
        setCurrentGTIN(null);
        setDatePeriod({ isMaxDate: true, fromDate: "", toDate: "" });
        setCustomSearchValue("");

        headlineKPIsRef?.current?.resetView();
        gtinKpisRef?.current?.resetView();
        kpiMainWindowHeaderRef?.current?.resetView();
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            onRefreshAction: () => handleUpdate(true),
            allStoresList,
            onStoresChange,
            onFiltersLoaded,
            datePeriod,
            setDatePeriod,
            navigate,
            setCurrentGTIN,
            kpiEnabled,
            dataAsOfDateTime,
            customSearchValue,
            setCustomSearchValue,
            currentProduct,
            resetPage,
            kpiMainWindowHeaderRef
        });
    }, [
        props?.layoutType,
        allStoresList,
        datePeriod,
        kpiEnabled,
        customSearchValue,
        currentProduct,
        kpiMainWindowHeaderRef
    ]); //Setup headers

    useEffect(() => {
        if (!requestsEnabled) {
            console.debug("Requests not enabled yet, skipping handleUpdate.");
            return;
        }

        // Skip re-fetch when filteredStores was populated for the first time by getKPIsRequest itself
        if (skipNextFilteredStoresUpdateRef.current) {
            skipNextFilteredStoresUpdateRef.current = false;
            return;
        }

        handleUpdate();
    }, [requestsEnabled, datePeriod, filteredStores, urlParamsProductID, urlParamsGtinID]); // After request are enabled then make api call "getKPIsRequest"

    useEffect(() => {
        if (urlParamsProductName) {
            const fixedName = decodeURIComponent(urlParamsProductName);
            setCustomSearchValue(fixedName);
        }
    }, [urlParamsProductName]);

    /*useEffect(() => {
        return () => {
            //on exit page set layout type to grid
            console.log("exit page");
            setTimeout(() => {
                setLayoutType("Grid");
            }, 5000);
        };
    }, []);*/

    useEffect(() => {
        return () => {
            if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current);
        };
    }, []);
    // #endregion

    // #region RENDER
    return (
        <>
            <FullScreenLoader isLoading={isLoadingRequest} />

            <StyledPage className="product-kpis-page">
                <HeadlineKPIs
                    ref={headlineKPIsRef}
                    lastUpdatedDateTime={lastUpdatedDateTime}
                    dataAsOfDateTime={dataAsOfDateTime}
                    kpiData={kpiData}
                    currentGTIN={currentGTIN}
                    onGtinClicked={onGtinClicked}
                    enabled={kpiEnabled}
                    datePeriod={datePeriod}
                    setDatePeriod={setDatePeriod}
                />

                <GTINsKPIs
                    ref={gtinKpisRef}
                    onGtinClicked={onGtinClicked}
                    kpiData={kpiData}
                    enabled={kpiEnabled}
                    highlightedStock={!datePeriod.isMaxDate && kpiEnabled}
                />
            </StyledPage>
        </>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    onRefreshAction,
    allStoresList,
    onStoresChange,
    onFiltersLoaded,
    datePeriod,
    setDatePeriod,
    navigate,
    setCurrentGTIN,
    kpiEnabled,
    dataAsOfDateTime,
    customSearchValue,
    setCustomSearchValue,
    currentProduct,
    resetPage,
    kpiMainWindowHeaderRef
}) {
    const productIDSuffix = currentProduct
        ? `?productID=${currentProduct.productID}&productName=${currentProduct.productName}`
        : "";

    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch
                    enabled={kpiEnabled}
                    placeholder="Item name, style nr, ean code"
                    onTextChange={setCustomSearchValue}
                    searchValue={customSearchValue}
                    noSearchResultsMessage="No product found"
                    autoCompleteAPIData={{
                        useBOCommon: true,
                        BOCommonContext: "Products",
                        apiName: "searchProducts",
                        apiSearchFieldName: "searchText",
                        includeInAPIFormData: { companyID: localStorage.getItem("currentCompanyID") },
                        customMapping: data => {
                            const isProducts = data?.isProducts;
                            const isGTIN = data?.isGTINs;

                            if ((!isProducts && !isGTIN) || !data) return [];

                            const searchResults = isProducts ? data?.products : data?.variants;

                            if (isProducts) {
                                const mappedData = searchResults?.map(item => ({
                                    ...item,
                                    isProduct: true,
                                    value: item._id,
                                    name: item.name
                                }));
                                return mappedData;
                            } else if (isGTIN) {
                                const mappedData = searchResults?.map(item => ({
                                    ...item,
                                    isGTINs: true,
                                    value: item._id,
                                    name: item.barcode,
                                    barcode: item.barcode
                                }));
                                return mappedData;
                            }
                        }
                    }}
                    onSelectedItemChange={item => {
                        if (!item) return;

                        if (item?.isProduct) {
                            navigate(`/products/metrics?productID=${item._id}&productName=${item.name}`);
                            setCurrentGTIN("All");
                        } else if (item?.isGTINs) {
                            navigate(`/products/metrics?gtinID=${item._id}`);
                            setCurrentGTIN(item.barcode);
                        }
                    }}
                    formFieldProps={{
                        requiredType: !kpiEnabled ? "mandatory" : "simple",
                        itemNotSelectedWillClear: true
                    }}
                    onClearSearch={() => resetPage()}
                />
            )
        }
    ]);
    props.setControlBarCenterBtns([
        { to: "/products", active: true, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: false, title: "MY DEFAULTS" }
    ]);
    props.setControlBarRightBtns([
        { to: "/products", active: false, disabled: false, title: "View", icon: "eye" },
        {
            to: `/products/stock${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Sales",
            icon: "bar-chart-05"
        },
        { to: null, active: true, disabled: false, title: "Metrics", icon: "bar-line-chart" },
        {
            to: `/products/details${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Details",
            icon: "microscope"
        },
        {
            to: `/products/pos-export${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Export",
            icon: "upload-01"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <StyledPage className="flex gap-16px space-between">
                <KpiMainWindowHeader
                    ref={kpiMainWindowHeaderRef}
                    handleUpdate={onRefreshAction}
                    datePeriod={datePeriod}
                    setDatePeriod={setDatePeriod}
                    dataAsOfDateTime={dataAsOfDateTime}
                    enabled={kpiEnabled}
                />

                <MainWindowHeader
                    key={window.location.pathname}
                    visible={true}
                    selections={{
                        visible: false
                    }}
                    layoutTypeOptions={{
                        gridEnabled: false,
                        listEnabled: kpiEnabled
                    }}
                    onRefreshClick={kpiEnabled ? onRefreshAction : undefined}
                    firstOrganize={{
                        disabled: !kpiEnabled,
                        visible: true,
                        dropdownData: allStoresList,
                        onDropdownChange: onStoresChange,
                        type: "multiSelect",
                        multidropdownOptions: {
                            labelAllSelected: "All stores",
                            labelNoneSelected: "",
                            allItems: allStoresList
                        },
                        noDataDisableTag: true,
                        tooltipTitle: "Filter stores",
                        selectedValue: !kpiEnabled ? "" : undefined
                    }}
                    secondOrganize={{
                        visible: false,
                        disabled: true
                    }}
                    search={{
                        visible: false
                    }}
                    pageFilters={{
                        useFilters: true,
                        filters: {
                            useConnectedLabelBrands: true,
                            useStores: true
                        },
                        onFiltersLoaded: onFiltersLoaded
                    }}
                    settingsButton={{
                        visible: true,
                        enabled: false
                    }}
                />
            </StyledPage>
        )
    });
}

// #endregion

// #region SMALL COMPONENTS
// eslint-disable-next-line no-unused-vars
const OrganizeBtnMenuComponent = props => {
    const { onItemClick } = props;

    return (
        <OrganizeBtnMenu
            items={[
                {
                    name: "Copy",
                    value: "copy"
                },
                {
                    name: "CSV",
                    value: "csv"
                },
                {
                    name: "Excel",
                    value: "excel"
                },
                {
                    name: "Print",
                    value: "print"
                },
                {
                    name: "PDF",
                    value: "pdf"
                }
            ]}
            onItemClick={onItemClick}
        />
    );
};

// #endregion
